// Auth endpoints: /api/v1/admin/auth/*: login and me, plus the signed-out
// routes the /invite, /forgot-password and /reset-password pages use: the
// staff-access invite routes (read / accept / claim) and 1.0's password reset
// (forgot-password / reset-password, the one reset league admins use too).

import { apiFetch } from './client'
import type { AcceptInviteResponse, ClaimInviteResponse, InviteLookup, LoginRequest, LoginResponse, Me } from './types'

/**
 * POST /admin/auth/login. A 401 here means bad credentials, so it is NOT
 * reported to the session-expiry handler. `portal: "platform"` makes the
 * server refuse a non-super-admin before it issues a token (403 not_super_admin).
 */
export function login(email: string, password: string): Promise<LoginResponse> {
  const body: LoginRequest = { email: email.trim(), password, portal: 'platform' }
  return apiFetch<LoginResponse>('/admin/auth/login', {
    method: 'POST',
    body,
    auth: false,
    handleUnauthorized: false,
  })
}

/** GET /admin/auth/me: revalidates a stored session on boot. */
export function me(signal?: AbortSignal): Promise<Me> {
  return apiFetch<Me>('/admin/auth/me', { signal })
}

// ─── Staff invites and password reset (registration plan S2) ────────────────
// Signed-out routes: no stored bearer and no session-expiry handling, so a
// stored Ops session is never sent to them and a 401 never signs anyone out.
// Errors carry a top-level `code`, which ApiError.code reads (invite_used,
// wrong_account, account_exists, validation_failed, …).

const signedOut = { auth: false, handleUnauthorized: false } as const

/** GET /admin/auth/invite?token= */
export function readInvite(token: string, signal?: AbortSignal): Promise<{ invite: InviteLookup }> {
  return apiFetch('/admin/auth/invite', { ...signedOut, query: { token }, signal })
}

/** POST /admin/auth/invite/accept: a NEW account (409 account_exists when the address has one). */
export function acceptInvite(body: { token: string; name: string; password: string }): Promise<AcceptInviteResponse> {
  return apiFetch('/admin/auth/invite/accept', { ...signedOut, method: 'POST', body })
}

/**
 * POST /admin/auth/login WITHOUT `portal`: the invitee is not a super admin
 * yet, so `portal: 'platform'` would refuse them (403 not_super_admin) before
 * they can claim the invite that makes them one. Nothing is stored here.
 */
export function signInForInvite(email: string, password: string): Promise<LoginResponse> {
  return apiFetch('/admin/auth/login', { ...signedOut, method: 'POST', body: { email: email.trim(), password } })
}

/** POST /admin/auth/invite/claim with the token signInForInvite just issued. */
export function claimInvite(token: string, bearer: string): Promise<ClaimInviteResponse> {
  return apiFetch('/admin/auth/invite/claim', { method: 'POST', body: { token }, bearer })
}

/**
 * POST /admin/auth/forgot-password: always the same 202 `{ message }` (400
 * only for a malformed address). `portal: 'platform'` asks for a super
 * admin's link, opening on Ops (`/reset-password#<token>`); without it the
 * link opens on a league's admin.
 */
export function forgotPassword(email: string, portal: 'platform' | null): Promise<{ message?: string }> {
  return apiFetch('/admin/auth/forgot-password', {
    ...signedOut,
    method: 'POST',
    body: portal ? { email, portal } : { email },
  })
}

/**
 * POST /admin/auth/reset-password: sets the password; issues no session.
 * 400 `validation_failed` (field `password`), or 400 with `reason:
 * 'invalid_token'` for an unknown, expired or used link.
 */
export function resetPassword(token: string, password: string): Promise<{ message?: string }> {
  return apiFetch('/admin/auth/reset-password', { ...signedOut, method: 'POST', body: { token, password } })
}
